import { Directive, HostBinding, HostListener, Input } from '@angular/core';
import type { Point } from 'geojson';
import { hasMapCoordinates } from '../../core/spatial/map-viewport';
import { MapNavigationService } from '../../core/map-navigation.service';
import { MapObjectReference } from '../../core/map-object';
import { MapObjectPopover } from '../../core/map-object-popover';
import { focusPinIcon, PIN_HEIGHT } from '../../core/place-pin';

/**
 * Show one record on the shared map, from whatever element a row offers it on.
 *
 * The action used to live inside the pin button and could only be taken there.
 * It is the same action wherever it is offered - a row's name asks the map for
 * the same record its pin does - so the button now wears this rather than the
 * name growing a second copy of it.
 *
 * Nothing here knows about lists, selection or routing. The page supplies the
 * business identity and the facts to show; Navigation owns focus and fits the
 * address into the map beside the Workspace.
 *
 * A record with no usable coordinates is NOT an error and not a dead click: the
 * host is left alone and the event goes on to whatever the row does with it, so
 * a name without a location still opens its record the way it always did.
 */
@Directive({ selector: '[appShowOnMap]', standalone: true, exportAs: 'showOnMap' })
export class ShowOnMapDirective {
  /** Which business record this is. Absent means there is nothing to show. */
  @Input('appShowOnMap') object?: MapObjectReference;
  @Input() latitude?: number | null;
  @Input() longitude?: number | null;
  @Input() popup?: MapObjectPopover;

  constructor(private readonly navigation: MapNavigationService) {}

  /**
   * Whether this record can be put on the map at all.
   *
   * Zero is a real coordinate and is admitted; a missing, infinite or
   * out-of-range one is not, and neither is a record with no identity or
   * nothing to say about itself in the popover.
   */
  get located(): boolean {
    return !!this.object && !!this.popup && hasMapCoordinates(this.latitude, this.longitude);
  }

  /** Marks a host that will act, so only that one is styled as offering it. */
  @HostBinding('class.map-locatable') get locatable(): boolean { return this.located; }

  @HostListener('click', ['$event'])
  locate(event: Event): void {
    if (!this.located) return;
    // The row beneath is a different action - opening the record - and one
    // click is one instruction, so asking the map does not also open it.
    event.stopPropagation();
    const geometry: Point = { type: 'Point', coordinates: [this.longitude!, this.latitude!] };
    void this.navigation.locate({
      reference: this.object!, geometry, popup: this.popup!,
      pointIcon: focusPinIcon(), popupOffset: [0, -PIN_HEIGHT],
    });
  }
}
